<template>
  <el-dialog
    v-model="dialogVisible"
    title="修改证书信息"
    width="600px"
    :before-close="handleCancel"
  >
    <el-form
      ref="editFormRef"
      :model="editForm"
      label-width="120px"
      :rules="editRules"
    >
      <!-- 序号不显示 -->
      
      <!-- 证书编号（不可修改） -->
      <el-form-item label="证书编号" prop="certificateCode">
        <el-input
          v-model="editForm.certificateCode"
          :disabled="true"
          style="background-color: #f5f7fa; cursor: not-allowed;"
        />
      </el-form-item>

      <!-- 证书单位 -->
      <!-- <el-form-item label="证书单位" prop="certificateCompanyName">
        <el-input v-model="editForm.certificateCompanyName" />
      </el-form-item> -->

      <!-- 器具名称 -->
      <!-- <el-form-item label="器具名称" prop="instrumentName">
        <el-input v-model="editForm.instrumentName" />
      </el-form-item> -->

      <!-- 型号规格 -->
      <!-- <el-form-item label="型号规格" prop="modelSpecification">
        <el-input v-model="editForm.modelSpecification" />
      </el-form-item> -->

      <!-- 出厂编号 -->
      <!-- <el-form-item label="出厂编号" prop="factorySerialCode">
        <el-input v-model="editForm.factorySerialCode" />
      </el-form-item> -->

      <!-- 最近更新时间（不可修改） -->
      <el-form-item label="最近更新时间" prop="updateTime">
        <el-input
          v-model="editForm.updateTime"
          :disabled="true"
          style="background-color: #f5f7fa; cursor: not-allowed;"
        />
      </el-form-item>

      <!-- 动态字段 -->
      <div v-if="editFormJsonData && editFormJsonData.length > 0">
        <el-form-item v-for="(item, index) in editFormJsonData" :key="index" :label="item.header">
          <el-input
            v-model="item.value"
            placeholder="请输入字段值"
          />
        </el-form-item>
      </div>
      <el-form-item v-else>
        <div class="json-data-empty">暂无动态信息</div>
      </el-form-item>
    </el-form>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup>
import { reactive, ref, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import certificateAPI from '@/api/certificate.js'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  certificateId: {
    type: Number,
    required: true
  },
  rowData: {
    type: Object,
    default: null
  }
})

// Emits
const emit = defineEmits(['update:visible', 'saved'])

// 表单相关
const editFormRef = ref(null)
const editForm = reactive({})
const editFormCopy = reactive({})
// 用于编辑jsonData的数组
const editFormJsonData = ref([])

// 表单验证规则
const editRules = {
  certificateCode: [
    { required: true, message: '证书编号不能为空', trigger: 'blur' }
  ],
  certificateCompanyName: [
    { required: true, message: '证书单位不能为空', trigger: 'blur' },
    { max: 50, message: '证书单位不能超过50个字符', trigger: 'blur' }
  ],
  instrumentName: [
    { required: true, message: '器具名称不能为空', trigger: 'blur' },
    { max: 50, message: '器具名称不能超过50个字符', trigger: 'blur' }
  ],
  modelSpecification: [
    { max: 30, message: '型号规格不能超过30个字符', trigger: 'blur' }
  ],
  factorySerialCode: [
    { max: 30, message: '出厂编号不能超过30个字符', trigger: 'blur' }
  ]
}

// 监听表单数据变化，同步更新editFormJsonData
watch(() => editForm.jsonData, (newJsonData) => {
  if (newJsonData) {
    try {
      editFormJsonData.value = JSON.parse(newJsonData)
    } catch (error) {
      console.error('解析jsonData失败:', error)
      editFormJsonData.value = []
    }
  } else {
    editFormJsonData.value = []
  }
}, { immediate: true })

// 监听对话框可见性变化和certificateId变化，获取证书详情
watch([() => props.visible, () => props.certificateId, () => props.rowData], async ([newVisible, newId, newRowData]) => {
  console.log('watch触发:', { newVisible, newId, newRowData })
  if (newVisible && newId && newId > 0) { // Only proceed if newId is a positive number
    try {
      // 优先使用从父组件传入的rowData
      if (newRowData) {
        console.log('使用父组件传入的rowData初始化表单')
        Object.assign(editForm, JSON.parse(JSON.stringify(newRowData)))
        Object.assign(editFormCopy, JSON.parse(JSON.stringify(newRowData)))
      } else {
        // 否则，仍然调用API获取数据
        console.log('调用API获取证书详情:', newId)
        // 设置一个计时器，3秒后显示加载提示
        let loadingMessage = null
        const loadingTimeout = setTimeout(() => {
          loadingMessage = ElMessage({ message: '正在加载证书信息...', type: 'info', duration: 0 })
        }, 3000)
        
        try {
          const response = await certificateAPI.getCertificateById(newId)
          // 清除计时器
          clearTimeout(loadingTimeout)
          // 关闭可能已显示的加载提示
          if (loadingMessage) {
            loadingMessage.close()
          }
          
          if (response) {
            Object.assign(editForm, JSON.parse(JSON.stringify(response)))
            Object.assign(editFormCopy, JSON.parse(JSON.stringify(response)))
          } else {
            ElMessage.error('获取证书信息失败：未找到数据')
            console.error('获取证书信息失败:', response)
            dialogVisible.value = false
          }
        } catch (apiError) {
          // 清除计时器
          clearTimeout(loadingTimeout)
          // 关闭可能已显示的加载提示
          if (loadingMessage) {
            loadingMessage.close()
          }
          // 重新抛出错误，让外层catch处理
          throw apiError
        }
      }
    } catch (error) {
      ElMessage.closeAll()
      ElMessage.error('获取证书信息失败：' + error.message || '未知错误')
      console.error('获取证书信息异常:', error)
      dialogVisible.value = false
    }
  } else if (newVisible && (!newId || newId <= 0)) {
    ElMessage.error('无效的证书ID')
    console.log('无效的证书ID:', { newId })
    dialogVisible.value = false
  } else {
    console.log('未满足调用条件:', { newVisible, newId })
  }
}, { immediate: true, deep: true })

// 监听可见性变化
const dialogVisible = computed({
  get() {
    return props.visible
  },
  set(value) {
    emit('update:visible', value)
  }
})

// 处理保存
  const handleSave = async () => {
    try {
      // 进行表单验证
      if (editFormRef.value) {
        const valid = await editFormRef.value.validate()
        if (!valid) {
          return
        }
      }
      
      // 更新修改时间
      editForm.updateTime = new Date().toISOString()
      
      // 将编辑的jsonData对象数组转换为字符串
      const saveData = JSON.parse(JSON.stringify(editForm))
      if (editFormJsonData.value && editFormJsonData.value.length > 0) {
        saveData.jsonData = JSON.stringify(editFormJsonData.value)
      }
      
      // 调用API更新证书信息
      // 注意：api.js的响应拦截器会在业务逻辑成功(code=0)时返回result.data
      // 如果业务逻辑失败，会抛出异常
      await certificateAPI.updateCertificate(saveData)
      
      // API调用成功，关闭对话框并通知父组件
      dialogVisible.value = false
      emit('saved')
    } catch (error) {
      ElMessage.error('保存失败：' + error.message || '未知错误')
      console.error('保存证书信息异常:', error)
    }
  }
  
  // 处理取消
  const handleCancel = () => {
    // 清空所有表单数据
    Object.keys(editForm).forEach(key => {
      delete editForm[key]
    })
    editFormJsonData.value = []
    
    // 直接关闭对话框，不调用接口，不显示提示
    dialogVisible.value = false
  }
</script>

<style scoped>
.json-data-empty {
  color: #909399;
  text-align: center;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
}
</style>

<style scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>